import { CookieConsentStatus } from 'constants/localStorage'
import { Region, SubscriptionTier, UserType } from 'generated/generated-graphql'

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-EYSP5YM5MQ'

export const isGoogleAnalyticsEnabled =
  process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production'

export enum AnalyticsLoginMethod {
  EmailAndPassword = 'email_and_password',
  FirebaseToken = 'firebase_token',
}

export enum AnalyticsTenderCreationSource {
  New = 'new',
  Draft = 'draft',
  CopiedTender = 'copied_tender',
}

interface TenderEventParameters {
  tender_id: string
  tender_name: string
}

interface TenderPublicationEventParameters extends TenderEventParameters {
  is_private: boolean
  product_category_count: number
}

/**
 * Every Google Analytics event the app sends, keyed by GA4 event name.
 * Parameters must be registered as custom dimensions/metrics in GA4 admin
 * before they show up in standard reports (they are always visible in
 * DebugView and Explorations).
 */
interface AnalyticsEventParameters {
  login: { method: AnalyticsLoginMethod }
  sign_up: {
    user_type: UserType
    region: Region
    subscription_tier?: SubscriptionTier
    is_invited: boolean
  }
  view_tender_detail: TenderEventParameters & { user_type: UserType }
  click_create_bid: TenderEventParameters
  click_submit_bid: TenderEventParameters
  successful_bid: TenderEventParameters & {
    bid_id: number
    currency: string
    // GA4 revenue metric, EzTenda earns the success fee
    value: number
    bid_value: number
    support_value: number
    fee_value: number
  }
  click_submit_automated_bid: {
    bid_id: number
    maximum_accepted_deals?: number | null
    product_name?: string
    product_category_id?: number
    region?: Region
  }
  save_tender_draft: {
    tender_name: string
    product_category_count: number
    creation_source: AnalyticsTenderCreationSource
  }
  publish_tender: TenderPublicationEventParameters & {
    creation_source: AnalyticsTenderCreationSource
  }
  // winning_bid_count 0 means the tender was closed without a winner
  choose_winner: TenderEventParameters & {
    winning_bid_count: number
    is_premature_closure: boolean
    has_feedback: boolean
  }
}

interface AnalyticsCompany {
  id: number
  name?: string | null
}

interface AnalyticsUser {
  id: number
  userType: UserType
  region: Region
  activeCompany?: AnalyticsCompany | null
  subscriptionTier?: SubscriptionTier | null
  venueCategoryName?: string | null
}

export const trackAnalyticsEvent = <
  EventName extends keyof AnalyticsEventParameters,
>(
  eventName: EventName,
  parameters: AnalyticsEventParameters[EventName]
) => {
  window.gtag?.('event', eventName, parameters)
}

/**
 * Attaches the signed in user and their active brand or venue to all
 * following events, so every GA4 report can be split by Brands and Venues.
 * Only internal ids and company names are sent, never personal data like
 * user email or contact name.
 */
export const setAnalyticsUser = (user: AnalyticsUser | null) => {
  window.gtag?.('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
    user_id: user ? String(user.id) : null,
    send_page_view: false,
  })
  window.gtag?.('set', 'user_properties', {
    user_type: user?.userType ?? null,
    region: user?.region ?? null,
    subscription_tier: user?.subscriptionTier ?? null,
    venue_category: user?.venueCategoryName ?? null,
  })
  // Event scoped, a user can switch between multiple companies
  window.gtag?.('set', {
    company_type: user?.activeCompany ? user.userType : null,
    company_id: user?.activeCompany ? String(user.activeCompany.id) : null,
    company_name: user?.activeCompany?.name ?? null,
  })
}

export const updateAnalyticsConsent = (status: CookieConsentStatus) => {
  window.gtag?.('consent', 'update', {
    analytics_storage: status,
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  })
}

// Google Analytics, Google Ads and Sopro cookies
const TRACKING_COOKIE_PREFIXES = ['_ga', '_gid', '_gcl', '_obid']

/**
 * Tracking cookies are set on the current host or one of its parent
 * domains, and can only be removed with the same domain they were set with.
 */
export const removeTrackingCookies = () => {
  const hostnameParts = window.location.hostname.split('.')
  const cookieDomains = hostnameParts.map(
    (_, index) => `.${hostnameParts.slice(index).join('.')}`
  )

  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim() ?? '')
    .filter((cookieName) =>
      TRACKING_COOKIE_PREFIXES.some((prefix) => cookieName.startsWith(prefix))
    )
    .forEach((cookieName) => {
      const expiredCookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      document.cookie = expiredCookie
      cookieDomains.forEach((cookieDomain) => {
        document.cookie = `${expiredCookie}; domain=${cookieDomain}`
      })
    })
}
